/**
 * Frontend-only catalog of consent categories — the purposes a visitor decides
 * about. The concrete technologies live in `consent-catalog.constants.ts` and
 * reference these keys; no Sanity configuration is involved.
 */
export const CONSENT_CATEGORIES = [
    {
        key: 'functional',
        label: 'Notwendig',
        description:
            'Technisch notwendige Cookies ermöglichen Grundfunktionen wie Seitennavigation und Sicherheit. Ohne diese Cookies funktioniert die Website nicht korrekt.',
        required: true,
    },
    {
        key: 'analytics',
        label: 'Statistik',
        description:
            'Hilft uns zu verstehen, wie die Website genutzt wird – anonymisiert und ausschließlich zur Verbesserung des Angebots (z. B. Matomo).',
        required: false,
    },
    {
        key: 'marketing',
        label: 'Marketing',
        description:
            'Wird genutzt, um Inhalte und Werbung relevanter zu gestalten und die Wirksamkeit von Kampagnen zu messen.',
        required: false,
    },
    {
        key: 'external-media',
        label: 'Externe Medien',
        description:
            'Ermöglicht das Anzeigen externer Inhalte wie Videos (z. B. YouTube). Beim Laden können Daten an die jeweiligen Anbieter übertragen werden.',
        required: false,
    },
    {
        key: 'spam-protection',
        label: 'Spamschutz',
        description:
            'Schützt Formulare vor automatisierten Einsendungen (z. B. Google reCAPTCHA). Dabei können Daten an den jeweiligen Anbieter übertragen werden.',
        required: false,
    },
] as const;

/**
 * localStorage key + version. Bump the version to invalidate stored consent —
 * only for structural changes to the payload. A new service does NOT need a
 * bump: it is detected by id and re-prompted without discarding the existing
 * decision (see `findUndecidedServices`).
 */
export const CONSENT_STORAGE_KEY = 'od-consent';
export const CONSENT_STORAGE_VERSION = 2;
/** An accepting decision is re-requested after this period (in days). */
export const CONSENT_TTL_DAYS = 180;
/** A rejecting decision is re-requested the next day. */
export const CONSENT_REJECTED_TTL_DAYS = 1;

/**
 * Mirror of "a decision exists" as a cookie. localStorage is invisible to the
 * server, which forced the banner to wait for hydration — and made its text the
 * LCP element with over a second of render delay. With the cookie the server
 * knows whether to render the banner at all, so it paints with the first frame
 * and never flashes for visitors who already decided.
 *
 * Split by outcome because the two lifetimes differ: an acceptance keeps the
 * banner away for CONSENT_TTL_DAYS, a rejection only for
 * CONSENT_REJECTED_TTL_DAYS. At most one of the two keys is ever set.
 *
 * They store the consent decision itself and are therefore technically necessary.
 */
export const CONSENT_COOKIE_ACCEPTED_KEY = 'od-consent-accepted';
export const CONSENT_COOKIE_REJECTED_KEY = 'od-consent-rejected';
export const CONSENT_COOKIE_VALUE = String(CONSENT_STORAGE_VERSION);

export const CONSENT_BANNER_COPY = {
    /** Shown when services were added after the visitor already decided. */
    updateTitle: 'Neue Dienste seit Ihrer letzten Auswahl',
    updateDescription:
        'Wir setzen inzwischen Technologien ein, über die wir Sie noch nicht informiert hatten. Sie sind bis zu Ihrer Entscheidung deaktiviert. Ihre bisherige Auswahl bleibt bestehen.',
    title: 'Wir respektieren Ihre Privatsphäre',
    description:
        'Wir verwenden Cookies und ähnliche Technologien, um diese Website bereitzustellen sowie Inhalte und Nutzung zu verbessern. Einige sind notwendig, andere helfen uns, das Angebot zu optimieren.',
    settingsTitle: 'Datenschutz-Einstellungen',
    settingsDescription:
        'Entscheiden Sie selbst, welche Technologien Sie zulassen möchten. Sie können Ihre Auswahl jederzeit über „Cookie-Einstellungen“ im Footer anpassen.',
    privacyPrefix: 'Weitere Informationen finden Sie in unserer',
    privacyLinkText: 'Datenschutzerklärung',
    acceptAll: 'Alle akzeptieren',
    rejectAll: 'Alle ablehnen',
    configure: 'Einstellungen',
    save: 'Auswahl speichern',
    back: 'Zurück',
    close: 'Schließen',
    alwaysActive: 'Immer aktiv',
    servicesLabel: 'Verwendete Dienste',
    showServices: 'Dienste anzeigen',
    hideServices: 'Dienste ausblenden',
} as const;
